import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from '../icons/Icon';
import { useMediaQuery } from '../lib/hooks';
import { Skeleton } from './Skeleton';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Cell content. Defaults to `row[key]` when the key matches a field. */
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  /** Value used for sorting when the column is sortable. */
  sortValue?: (row: T) => string | number;
  align?: 'start' | 'center' | 'end';
  width?: string;
  /** Extra className on the <td>. */
  cellClassName?: string;
}

export type SortDirection = 'asc' | 'desc';

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  defaultSort?: { key: string; direction: SortDirection };
  loading?: boolean;
  skeletonRows?: number;
  /** Shown when there are no rows and not loading. */
  empty?: ReactNode;
  /** Renders one card per row below `stackBelow` (brief mobile table-stack). */
  renderCard?: (row: T) => ReactNode;
  stackBelow?: number;
  caption?: string;
  className?: string;
}

/** Sortable table with a card/stack fallback on small screens (brief §4.2/§7.1). */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  defaultSort,
  loading = false,
  skeletonRows = 4,
  empty,
  renderCard,
  stackBelow = 640,
  caption,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; direction: SortDirection } | undefined>(defaultSort);
  const isStack = useMediaQuery(`(max-width: ${stackBelow}px)`) && !!renderCard;

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortable) return rows;
    const value = col.sortValue ?? ((row: T) => (row as Record<string, unknown>)[col.key] as string | number);
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [rows, sort, columns]);

  const toggleSort = (key: string) =>
    setSort((prev) =>
      prev?.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );

  const cellContent = (col: Column<T>, row: T): ReactNode =>
    col.render ? col.render(row) : ((row as Record<string, unknown>)[col.key] as ReactNode);

  if (!loading && rows.length === 0 && empty) {
    return <div className={className}>{empty}</div>;
  }

  // Mobile card stack
  if (isStack) {
    return (
      <div className={cx('tc-cardlist', className)}>
        {loading
          ? Array.from({ length: skeletonRows }).map((_, i) => (
              <div key={i} className="tc-cardlist__item" style={{ cursor: 'default' }}>
                <Skeleton width="55%" />
                <Skeleton width="35%" style={{ marginTop: 8 }} />
              </div>
            ))
          : sortedRows.map((row, i) => (
              <div
                key={rowKey(row, i)}
                className="tc-cardlist__item"
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {renderCard!(row)}
              </div>
            ))}
      </div>
    );
  }

  return (
    <div className={cx('tc-table-wrap', className)}>
      <table className="tc-table">
        {caption && <caption className="tc-visually-hidden">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => {
              const active = sort?.key === col.key;
              const ariaSort = active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <th
                  key={col.key}
                  scope="col"
                  style={{ width: col.width, textAlign: col.align }}
                  aria-sort={col.sortable ? ariaSort : undefined}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      className="tc-table__sort"
                      aria-sort={ariaSort}
                      onClick={() => toggleSort(col.key)}
                    >
                      {col.header}
                      <Icon
                        className="tc-table__sort-icon"
                        name={!active ? 'chevron-up-down' : sort!.direction === 'asc' ? 'chevron-up' : 'chevron-down'}
                        size={12}
                        strokeWidth={2.5}
                      />
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      <Skeleton width={`${50 + ((i * 7 + col.key.length * 5) % 40)}%`} />
                    </td>
                  ))}
                </tr>
              ))
            : sortedRows.map((row, i) => (
                <tr
                  key={rowKey(row, i)}
                  className={cx(onRowClick && 'tc-table__row--clickable')}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter') onRowClick(row);
                        }
                      : undefined
                  }
                >
                  {columns.map((col) => (
                    <td key={col.key} style={{ textAlign: col.align }} className={col.cellClassName}>
                      {cellContent(col, row)}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
